/**
 * Route-level analytics hook.
 *
 * Drives PostHog pageviews manually because this is a wouter SPA — posthog-js's
 * built-in `capture_pageview` only fires on the initial document load, so
 * client-side navigation would otherwise be invisible.
 *
 * Mounted once in App.tsx's Router(). Event plumbing lives in `@/lib/posthog`.
 */

import { useLocation } from 'wouter';
import { useEffect, useCallback, useRef } from 'react';
import { debug } from '@/lib/logger';
import { capture, capturePageView, EVENTS } from '@/lib/posthog';
import { trackMetaPageView } from '@/lib/metaPixel';

export function useAnalytics() {
  const [location] = useLocation();
  // One pageview per navigation. React StrictMode double-invokes effects in
  // development, which otherwise sent $pageview twice for a single page load
  // and made every local funnel read about twice as high as reality.
  const lastCapturedLocation = useRef<string | null>(null);

  // The Meta pixel needs the same treatment for the same reason, but with one
  // extra wrinkle: `initMetaPixel` already fires PageView once, as Meta's base
  // code specifies. Firing again here for the very first location the hook sees
  // would double-count the entry page of every session -- so the first location
  // is deliberately skipped and only real client-side navigations report.
  //
  // This holds when consent arrives late, too: the pixel initialises on
  // whatever page consent was granted on and fires PageView for it, while this
  // hook (which has been no-opping without consent) simply continues from the
  // next navigation.
  //
  // Without this, `trackMetaPageView` was exported and called from nowhere, so
  // Meta saw exactly one pageview per session no matter how far a visitor
  // browsed -- which also silently breaks URL-based custom conversions, since
  // those are matched against the referrer URLs of PageView events.
  const hasSeenFirstMetaLocation = useRef(false);

  useEffect(() => {
    debug('Page view', { location });
    if (lastCapturedLocation.current === location) return;
    lastCapturedLocation.current = location;
    capturePageView(location);

    if (!hasSeenFirstMetaLocation.current) {
      hasSeenFirstMetaLocation.current = true;
      return;
    }
    trackMetaPageView();
  }, [location]);

  const setWorld = useCallback((world: 'sports' | 'fantasy') => {
    debug('World selected', { world });
    capture(EVENTS.WORLD_SELECTED, { world });
  }, []);

  return { setWorld };
}
